import { useMemo } from "react";

import { useDebounceValue } from "usehooks-ts";

import { useSchemas } from "src/datasets/utils";
import {
  FieldMatch,
  findMatchingFields,
} from "src/flowGraph/canvasSearch/extractNodeFields";
import { findMatchingParameters } from "src/flowGraph/canvasSearch/extractParameters";
import { findMatchingSchemaFields } from "src/flowGraph/canvasSearch/extractSchemaFields";
import { useCanvasSearchTermParam } from "src/flowGraph/useCanvasSearchTermParam";
import { useAuthoringContext } from "src/router/routerContextHooks";
import { SchemaOptions } from "src/router/SearchParams";
import { useGraphStore } from "src/store/StoreProvider";
import { orderNodesAndEdgesForLayout } from "src/utils/GraphUtils";

const DEBOUNCE_MS = 400;
const MIN_SEARCH_LENGTH = 2;

type CanvasSearchResult = {
  searchTerm: string;
  debouncedSearchTerm: string;
  matches: FieldMatch[];
  isSearching: boolean;
};

export const useCanvasSearch = (): CanvasSearchResult => {
  const { searchTerm } = useCanvasSearchTermParam();
  const [debouncedSearchTerm] = useDebounceValue(searchTerm, DEBOUNCE_MS);
  const graphStore = useGraphStore();
  const { version } = useAuthoringContext();
  const { input, output } = useSchemas(version);

  const [orderedNodes] = useMemo(() => {
    return orderNodesAndEdgesForLayout(
      graphStore.nodesArray,
      graphStore.edgesArray,
    );
  }, [graphStore.nodesArray, graphStore.edgesArray]);

  const matches = useMemo(() => {
    if (
      !debouncedSearchTerm ||
      debouncedSearchTerm.length < MIN_SEARCH_LENGTH
    ) {
      return [];
    }

    const inputSchemaMatches = findMatchingSchemaFields({
      schema: input,
      searchTerm: debouncedSearchTerm,
      schemaType: SchemaOptions.Input,
      nodeId: graphStore.inputNode?.id ?? "",
    });
    const outputSchemaMatches = findMatchingSchemaFields({
      schema: output,
      searchTerm: debouncedSearchTerm,
      schemaType: SchemaOptions.Output,
      nodeId: graphStore.outputNode?.id ?? "",
    });

    const nodeMatches = orderedNodes.flatMap((node) =>
      findMatchingFields(
        node,
        debouncedSearchTerm,
        graphStore.getNodeToIncomingEdge,
      ),
    );
    const paramMatches = findMatchingParameters(
      version.parameters ?? [],
      debouncedSearchTerm,
    );

    return [
      ...inputSchemaMatches,
      ...outputSchemaMatches,
      ...paramMatches,
      ...nodeMatches,
    ];
  }, [
    orderedNodes,
    version.parameters,
    debouncedSearchTerm,
    input,
    output,
    graphStore.getNodeToIncomingEdge,
    graphStore.inputNode?.id,
    graphStore.outputNode?.id,
  ]);

  const isSearching = searchTerm !== debouncedSearchTerm;

  return {
    searchTerm,
    debouncedSearchTerm,
    matches,
    isSearching,
  };
};
